import FocusModalProvider, { useFocusModal } from "@/components/FocusModal/FocusModalContext";
import { useGraphVisualisation } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import { RuleGraphCtx } from "@/components/GraphVisualisation/providers/RuleGraphProvider";
import { useDownload } from "@/hooks/use-download";
import { countJsonNodes, generateJsonChunks } from "@/lib/downloads/jsonBlob";
import { useAttributeSelected } from "@imminently/decisively-ui/AttributeSelectedProvider";
import Button from "@imminently/decisively-ui/Button";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Icon from "@imminently/decisively-ui/Icon";
import Select from "@imminently/decisively-ui/Select";
import TextField from "@imminently/decisively-ui/TextField";
import Tree from "@imminently/decisively-ui/Tree";
import { debounce, get } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import type { DecisionGraph } from "@imminently/decisively-core";
import type { VisibleMask } from "@/components/GraphVisualisation/decisionGraphView";
import {
  buildDecisionReportTree,
  type ReportTreeNode,
  stripRuleDetailFields,
} from "./buildDecisionReportTree";
import ReportNode from "./ReportNode";
import { WithSortableTreeRefCtx } from "./SortableTreeRefCtx";

type DecisionReportProps = {
  goal: string;
  graph: DecisionGraph;
  /** The ids of `graph` in the view; null or undefined for every node. */
  visible?: VisibleMask;
  overscanCount?: number;
};

function changeAllNodes(node: ReportTreeNode, expanded = true, maxLevel?: string, curLevel = 0): ReportTreeNode {
  const maxLevelNumber = maxLevel ? Number(maxLevel) : undefined;
  return {
    ...node,
    expanded: maxLevelNumber && curLevel >= maxLevelNumber ? false : expanded,
    children: Array.isArray(node.children)
      ? node.children.map((it) => changeAllNodes(it, expanded, maxLevel, curLevel + 1))
      : undefined,
  };
}

const DecisionReportCore = ({ goal, graph, visible, overscanCount }: DecisionReportProps) => {
  const [searchTerm, setSearchTermRaw] = useState("");
  const [searchTermDebounced, setSearchTermDebouncedRaw] = useState("");
  const setSearchTermDebounced = useMemo(() => debounce(setSearchTermDebouncedRaw, 500), [setSearchTermDebouncedRaw]);
  const setSearchTerm = useCallback(
    (search: string) => {
      setSearchTermRaw(search);
      setSearchTermDebounced(search);
    },
    [setSearchTermRaw, setSearchTermDebounced],
  );

  const [collapseTo, setCollapseTo] = useState("1");
  const [foundCount, setFoundCount] = useState(0);
  const [focusOffset, setFocusOffset] = useState(0);
  const treeApiRef = useRef<any>(null);
  const ignoreNextSelectedNodeSearchRef = useRef(false);
  const { selectedNodeId, selectedNode, setSelectedNodeId } = useGraphVisualisation({
    selectedNodeId: null,
    selectedNode: null,
  });

  // need to construct the tree data
  const [tree, setTree] = useState<ReportTreeNode[]>([]);
  useEffect(() => {
    setTree(buildDecisionReportTree(graph, goal, { includeRules: true, visible }));
  }, [goal, graph, visible]);

  const expandAll = () => {
    setTree([changeAllNodes(tree[0])]);
  };
  const collapseAll = () => {
    setTree([changeAllNodes(tree[0], false)]);
  };
  const { decisionReportDownloadContext } = useContext(RuleGraphCtx);
  const downloadBaseName = useMemo(() => {
    const modelName = decisionReportDownloadContext?.modelName || "Unknown Model";
    const releaseNo = decisionReportDownloadContext?.releaseNo ?? "unknown";
    const sessionId = decisionReportDownloadContext?.sessionId ?? "unknown-session";

    // downloadFile() sanitizes the filename itself, so this doesn't need to.
    return `${modelName} - R${releaseNo} - ${sessionId} - Decision Report`;
  }, [decisionReportDownloadContext]);
  const downloadRawJson = useDownload(
    (onProgress, includeRules = false) => {
      if (tree.length === 0) return null;

      const report = includeRules ? tree : stripRuleDetailFields(tree);

      // Reports of this size are highly repetitive (shared keys/descriptions), so zipping
      // shrinks them drastically — a ~2.7GB report compresses to ~46MB. That, combined with
      // streaming straight to disk one chunk at a time where supported, means neither the
      // uncompressed nor compressed payload ever has to sit fully in memory, avoiding the
      // out-of-memory tab crash large reports hit otherwise.

      // countJsonNodes is a cheap structural pass (no stringifying, ~tens of ms even for
      // million-node reports) used purely as the denominator for a real progress percentage.
      const totalNodes = countJsonNodes(report);
      return {
        filename: `${downloadBaseName}.json`,
        zip: true,
        content: generateJsonChunks(report, 2, undefined, ({ nodesWritten }) =>
          onProgress(totalNodes > 0 ? nodesWritten / totalNodes : 1),
        ),
      };
    },
    {
      pending: "Downloading decision report",
      success: "Decision report downloaded",
      failure: "Failed to download decision report",
    },
  );
  const handleCollapseTo = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value) {
      setTree([changeAllNodes(tree[0], true, value)]);
      setCollapseTo(value);
    }
  };
  const selectNextMatch = useCallback(
    () => setFocusOffset((focusOffset) => (focusOffset !== null ? (focusOffset + 1) % foundCount : 0)),
    [foundCount],
  );
  const selectPrevMatch = useCallback(
    () =>
      setFocusOffset((focusOffset) =>
        focusOffset !== null ? (foundCount + focusOffset - 1) % foundCount : foundCount - 1,
      ),
    [foundCount],
  );
  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        selectNextMatch();
      }
    },
    [selectNextMatch],
  );
  const clearSearch = useCallback(() => {
    setSearchTermDebounced.cancel();
    setSearchTermRaw("");
    setSearchTermDebouncedRaw("");
    setFocusOffset(0);
  }, [setSearchTermDebounced, setSearchTermDebouncedRaw]);

  useEffect(() => () => setSearchTermDebounced.cancel(), [setSearchTermDebounced]);

  const { openFocusModal } = useFocusModal();
  const attributeSelectedContext = useAttributeSelected();

  const displayModal = useCallback(
    (node: ReportTreeNode) => {
      const path = node.node?.path || node.node?.id;
      if (path && setSelectedNodeId) {
        ignoreNextSelectedNodeSearchRef.current = true;
        setSelectedNodeId(path);
      }

      if (node.node && attributeSelectedContext?.onSelectAttribute) {
        attributeSelectedContext.onSelectAttribute(node.node as any);
        return;
      }

      openFocusModal(node.node);
    },
    [openFocusModal, attributeSelectedContext, setSelectedNodeId],
  );

  useEffect(() => {
    if (!selectedNodeId) return;
    if (ignoreNextSelectedNodeSearchRef.current) {
      ignoreNextSelectedNodeSearchRef.current = false;
      return;
    }

    const search = selectedNode?.description;
    if (!search) return;
    setSearchTermDebounced.cancel();
    setSearchTermRaw(search);
    setSearchTermDebouncedRaw(search);
    setFocusOffset(0);
  }, [selectedNodeId, selectedNode?.description, setSearchTermDebounced, setSearchTermDebouncedRaw]);

  const matchNode = useCallback((node: ReportTreeNode, term: string) => {
    if (!term) return false;
    const cmp = get(node, "node.description");
    if (!cmp) return false;
    return String(cmp).toLowerCase().includes(String(term).toLowerCase());
  }, []);

  useEffect(() => {
    const term = searchTermDebounced;
    let matches = 0;
    const walk = (nodes: ReportTreeNode[]) => {
      for (const n of nodes || []) {
        if (matchNode(n, term)) matches += 1;
        if (Array.isArray(n.children) && n.children.length > 0) walk(n.children);
      }
    };
    walk(tree);
    setFoundCount(matches);
    setFocusOffset((curr) => (matches > 0 ? curr % matches : 0));
  }, [tree, searchTermDebounced, matchNode]);

  const toUiTree = useCallback(
    (nodes: ReportTreeNode[], prefix = "d") =>
      (nodes || []).map((n, i) => ({
        id: `${prefix}-${n?.node?.path || i}`,
        label: null,
        info: { legacyNode: n, path: [String(i)] },
        children: toUiTree(n?.children || [], `${prefix}-${i}`),
      })),
    [],
  );

  const uiTreeData = useMemo(() => toUiTree(tree), [tree, toUiTree]);
  const initialExpandedState = useMemo(() => {
    const expanded: Record<string, boolean | undefined> = {};
    const walk = (nodes: ReportTreeNode[], prefix = "d") => {
      (nodes || []).forEach((n, i) => {
        const uiId = `${prefix}-${n?.node?.path || i}`;
        expanded[uiId] = n?.expanded;
        if (Array.isArray(n.children) && n.children.length > 0) walk(n.children, `${prefix}-${i}`);
      });
    };
    walk(tree);
    return expanded;
  }, [tree]);
  const [expandedState, setExpandedState] = useState<Record<string, boolean | undefined>>({});
  useEffect(() => {
    setExpandedState(initialExpandedState);
  }, [initialExpandedState]);

  const matchedUiEntries = useMemo(() => {
    const term = searchTermDebounced;
    if (!term) return [] as { id: string; path: number[] }[];
    const entries: { id: string; path: number[] }[] = [];
    const walk = (nodes: ReportTreeNode[], prefix = "d") => {
      (nodes || []).forEach((n, i) => {
        const uiId = `${prefix}-${n?.node?.path || i}`;
        if (matchNode(n, term)) entries.push({ id: uiId, path: [i] });
        if (Array.isArray(n.children) && n.children.length > 0) {
          const childEntriesBefore = entries.length;
          walk(n.children, `${prefix}-${i}`);
          for (let idx = childEntriesBefore; idx < entries.length; idx += 1) {
            entries[idx] = { ...entries[idx], path: [i, ...entries[idx].path] };
          }
        }
      });
    };
    walk(tree);
    return entries;
  }, [tree, searchTermDebounced, matchNode]);

  const matchedUiIds = useMemo(() => matchedUiEntries.map((it) => it.id), [matchedUiEntries]);

  const focusedMatchUiId = useMemo(() => {
    if (matchedUiIds.length === 0) return null;
    return matchedUiIds[((focusOffset % matchedUiIds.length) + matchedUiIds.length) % matchedUiIds.length];
  }, [matchedUiIds, focusOffset]);
  const hasSearch = Boolean(searchTermDebounced);
  const showSearchNavigation = hasSearch;

  const scrollToNode = useCallback((id: string, options?: { expand?: boolean }) => {
    if (options?.expand) treeApiRef.current?.openParents?.(id);
    treeApiRef.current?.scrollTo?.(id);
  }, []);

  useEffect(() => {
    if (!focusedMatchUiId) return;
    scrollToNode(focusedMatchUiId, { expand: true });
  }, [focusedMatchUiId, scrollToNode]);

  const handleSelect = useCallback(
    (selectedId: string) => {
      const findById = (nodes: any[]): any | null => {
        for (const node of nodes || []) {
          if (node?.id === selectedId) return node;
          const found = findById(node?.children || []);
          if (found) return found;
        }
        return null;
      };
      const selectedNode = findById(uiTreeData as any[]);
      const legacyNode = selectedNode?.info?.legacyNode;
      if (legacyNode) displayModal(legacyNode);
    },
    [uiTreeData, displayModal],
  );

  return (
    <div className="flex h-full w-full grow flex-col flex-nowrap pt-2">
      <div className="flex flex-wrap items-center gap-2 px-8 py-2">
        <div className="flex items-center gap-4">
          <div onKeyDown={handleSearchKeyDown}>
            <TextField
              name="decision-report-search"
              placeholder="Search report..."
              value={searchTerm}
              onValueChange={setSearchTerm}
              clearable
              onClear={clearSearch}
              clearAriaLabel="Clear search"
              className="w-80"
              fullWidth
            />
          </div>
          {showSearchNavigation ? (foundCount > 0 ? `${focusOffset + 1}/${foundCount}` : "0/0") : null}
          {showSearchNavigation ? (
            <div className="flex flex-shrink-0 gap-1">
              <Button
                icon
                label="Previous match"
                disabled={foundCount === 0}
                onClick={selectPrevMatch}
              >
                <Icon name="go-back" />
              </Button>
              <Button
                icon
                label="Next match"
                disabled={foundCount === 0}
                onClick={selectNextMatch}
              >
                <Icon name="go-forward" />
              </Button>
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-1 flex-shrink-0">
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button
                  icon
                  disabled={tree.length === 0}
                  label="Download raw JSON"
                >
                  <Icon name="download" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content align="end">
                <DropdownMenu.Item onClick={() => downloadRawJson(false)}>
                  <DropdownMenu.Item.Label>Download</DropdownMenu.Item.Label>
                  <DropdownMenu.Item.Icon>
                    <Icon name="download" />
                  </DropdownMenu.Item.Icon>
                </DropdownMenu.Item>
                <DropdownMenu.Item onClick={() => downloadRawJson(true)}>
                  <DropdownMenu.Item.Label>Download (with rules)</DropdownMenu.Item.Label>
                  <DropdownMenu.Item.Icon>
                    <Icon name="download" />
                  </DropdownMenu.Item.Icon>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
            <Button
              variant="contained"
              intent="secondary"
              onClick={expandAll}
              label="Expand All"
            >
              Expand All
            </Button>
            <Button
              variant="contained"
              intent="secondary"
              onClick={collapseAll}
              label="Collapse All"
            >
              Collapse All
            </Button>
            <Select
              value={collapseTo || "1"}
              options={["1", "2", "3", "4", "5", "6", "7", "8", "9", "10+"].map((it) => ({ value: it, label: it }))}
              onChange={handleCollapseTo}
              placeholder="Collapse to"
              className="w-20"
            />
          </div>
        </div>
      </div>
      <Tree
        className="[&_[data-deci-tree='true']]:h-full [&_[data-deci-tree='true']]:overflow-overlay"
        rowHeight={82}
        data={uiTreeData}
        showIndentBorders={false}
        selectedId={focusedMatchUiId}
        handleRef={treeApiRef}
        expandedState={expandedState}
        onExpandedStateChange={setExpandedState}
        openByDefault={true}
        overscanCount={overscanCount}
        onSelect={handleSelect}
      >
        {(p) => {
          const n = p.node.data?.info?.legacyNode;
          if (!n) return null;
          const term = searchTermDebounced;
          const isMatch = matchNode(n, term);
          const isFocusedMatch = String(p.node.data?.id) === focusedMatchUiId;
          return (
            <Tree.Item {...p}>
              <ReportNode
                graph={graph as any}
                node={n}
                expanded={p.node.isOpen}
                isSearchMatch={isMatch}
                isSearchFocus={isFocusedMatch}
                // Use arborist's toggle directly — updating expandedState would change treeKey and remount the tree, losing scroll position.
                toggleChildrenVisibility={() => p.node.toggle()}
              />
            </Tree.Item>
          );
        }}
      </Tree>
    </div>
  );
};

const DecisionReport = (props: DecisionReportProps) => (
  <FocusModalProvider
    graph={props.graph as any}
    visible={props.visible}
  >
    <WithSortableTreeRefCtx>
      <DecisionReportCore {...props} />
    </WithSortableTreeRefCtx>
  </FocusModalProvider>
);

export default DecisionReport;
